import { Composition } from 'atomic-layout';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { generatePath, useNavigate, useSearchParams } from 'react-router-dom';

import { NoDataSelected } from '../../../app/components/no-tag-system-selected';
import { Role } from '../../../app/constants/auth';
import { Paths } from '../../../app/constants/paths';
import { AuthorizationContext } from '../../../app/context/authorization-context';
import { useTenant } from '../../../app/context/tenant-context';
import { useGetUserSharedViewsV2, useGetUserViewsV2 } from '../../../app/hooks/accounts';
import { useGetAlertByID } from '../../../app/hooks/alerts';
import {
  FolderSort,
  usePlantFolders,
  useSubSystemsById,
  useTagsByName,
} from '../../../app/hooks/folders';
import { useGetView } from '../../../app/hooks/view';
import { TenantSelector } from '../../components/header';
import { Loading } from '../../components/loading/loading';
import AlertFolderTree from '../../components/system-explorer/AlertFolderTree';
import SearchBar from '../../components/system-explorer/SearchBar';
import { TagExplorerProvider } from '../../context/tag-explorer-context';

import { AlertConfiguration } from './alert-configurations';
import { AlertsV2Wrapper, areasMobile, Frame, SidePanel, ViewAlertContainer } from './style';

import { Container } from '@controlrooms/components';
import { PersistView, SubSystem, Tag } from '@controlrooms/models';

export enum TARGET_TYPE {
  SYSTEM = 'SYSTEM',
  TAG = 'TAG',
  VIEW = 'VIEW',
}

export const AlertManager: React.FC = () => {
  const { hasRole } = useContext(AuthorizationContext);

  const { data: userSharedViews } = useGetUserSharedViewsV2();
  const { data: _userViews } = useGetUserViewsV2();

  const userViews = useMemo(() => {
    return _userViews?.filter((v) => v.default_view === false) || [];
  }, [_userViews]);

  const viewList =
    hasRole(Role.GLOBAL_ALERT_EDITOR) || hasRole(Role.GLOBAL_ALERT_VIEWER)
      ? [...(userViews || []), ...(userSharedViews || [])]
      : userViews;

  const {
    isLoading: isFoldersLoading,
    isError: isFoldersError,
    data: folders,
  } = usePlantFolders(FolderSort.DEFAULT);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { data: systemsById } = useSubSystemsById();
  const { data: tagsByName } = useTagsByName();
  const { tenant: currentTenantId } = useTenant();

  const queryClient = useQueryClient();
  const _folderId = searchParams.get('folderId');
  const _viewId = searchParams.get('viewId');
  const _ruleId = searchParams.get('ruleId');

  const [selectedTags, setSelectedTags] = useState<Record<number, Tag[]>>({});
  const [selectedSystems, setSelectedSystems] = useState<SubSystem[]>([]);
  const [selectedView, setSelectedView] = useState<PersistView | undefined>(undefined);

  const { data: currPersistView } = useGetView(_viewId || '');

  // Fetch alert data when ruleId exists (from URL query param)
  const { data: alertDataResult, isLoading: isAlertDataLoading } = useGetAlertByID(_ruleId || '');
  const alertData = alertDataResult?.result;
  const alertSummary = alertData?.alert_view_summary;
  const targetType = alertData?.target_type;
  const targetId = alertData?.target_id;

  const folderId = useMemo(
    () => selectedSystems[0]?.folder || (_folderId as unknown as number),
    [_folderId, selectedSystems],
  );

  // Effect: Restore state from URL parameters (for deep linking)
  useEffect(() => {
    // Only restore state when _ruleId exists (deep link scenario)
    if (!_ruleId) return;

    if (targetType === TARGET_TYPE.SYSTEM.toLowerCase() && targetId) {
      setSelectedSystems([systemsById[Number(targetId)] as SubSystem]);
      setSelectedTags({
        [Number(targetId)]: [],
      });
    } else if (targetType === TARGET_TYPE.TAG.toLowerCase() && targetId && _folderId) {
      setSelectedSystems([systemsById[Number(_folderId)] as SubSystem]);
      if (Object.keys(tagsByName).length && targetId) {
        setSelectedTags({
          [Number(_folderId)]: [tagsByName[targetId]],
        });
      }
    } else if (targetType === TARGET_TYPE.VIEW.toLowerCase() && _viewId) {
      setSelectedSystems([]);
      setSelectedTags({});
      setSelectedView(currPersistView);
    }
  }, [_ruleId, _folderId, _viewId, targetType, targetId, systemsById, tagsByName, currPersistView]);

  const handleCloseAlertConfig = useCallback(() => {
    // Clear URL query params first - this prevents useEffect from re-triggering
    navigate(
      {
        pathname: generatePath(Paths.MANAGE_ALERTS, {
          tenantId: currentTenantId.toString(),
        }),
        search: '',
      },
      { replace: true }, // Use replace to avoid adding history entry
    );

    // Clear React Query cache
    queryClient.removeQueries(['ALERT_CONFIGS']);

    // Clear component state
    setSelectedTags({});
    setSelectedSystems([]);
    setSelectedView(undefined);
  }, [queryClient, navigate, currentTenantId]);

  const alertType = useMemo(() => {
    if (folderId) {
      if (selectedTags[folderId]?.length === 0) return TARGET_TYPE.SYSTEM;
      else if (selectedTags[folderId]?.length > 0) return TARGET_TYPE.TAG;
    } else if (selectedView) return TARGET_TYPE.VIEW;
  }, [folderId, selectedTags, selectedView]);

  return (
    <AlertsV2Wrapper>
      <Container isPage>
        <Composition
          areas={areasMobile}
          areasLg={areasMobile}
          templateCols="auto"
          templateColsLg={`${320}px 1fr`}
          templateRows="auto"
          flexGrow="1"
        >
          {(Areas) => (
            <>
              <Areas.Sidebar>
                <TagExplorerProvider>
                  <SidePanel className="side-panel">
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <TenantSelector />
                    </div>
                    <SearchBar />
                    {isFoldersLoading && <div>Loading...</div>}
                    {!folders && isFoldersError && <div>Something went wrong</div>}
                    {!isFoldersLoading && folders && (
                      <div className="folder-tree-section">
                        <AlertFolderTree
                          preSelectedTags={{}}
                          defaultSelectedTags={selectedTags}
                          multiselect={false}
                          systemSelectable={true}
                          folders={folders?.subfolders as SubSystem[]}
                          views={viewList}
                          onSelectTags={(_selectedTags, _selectedSystems, _selectedView) => {
                            queryClient.removeQueries(['ALERT_CONFIGS']);
                            navigate({
                              pathname: generatePath(Paths.MANAGE_ALERTS, {
                                tenantId: currentTenantId.toString(),
                              }),
                              search: '',
                            });
                            if (_selectedView) {
                              setSelectedView(_selectedView);
                              setSelectedTags({});
                              setSelectedSystems([]);
                              return;
                            }
                            _selectedTags && setSelectedTags(_selectedTags);
                            _selectedSystems && setSelectedSystems(_selectedSystems);
                          }}
                        />
                      </div>
                    )}
                  </SidePanel>
                </TagExplorerProvider>
              </Areas.Sidebar>
              <Areas.Content>
                {!isFoldersLoading && folders && (
                  <ViewAlertContainer>
                    <Frame>
                      {isAlertDataLoading ? (
                        <Loading overlay small={true} />
                      ) : (
                        <>
                          {alertType && (folderId || selectedView) ? (
                            <AlertConfiguration
                              alertType={alertType}
                              view={selectedView}
                              systemId={folderId}
                              tags={selectedTags[folderId] || []}
                              selectedRule={_ruleId ? Number(_ruleId) : undefined}
                              alertSummary={alertSummary}
                              handleCloseForm={handleCloseAlertConfig}
                            />
                          ) : (
                            <NoDataSelected>
                              <h1>Select a tag or system at left</h1>
                              <p>to view alert configurations</p>
                            </NoDataSelected>
                          )}
                        </>
                      )}
                    </Frame>
                  </ViewAlertContainer>
                )}
              </Areas.Content>
            </>
          )}
        </Composition>
      </Container>
    </AlertsV2Wrapper>
  );
};
